import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import api from '../../utils/api';
import WinnerCongratulationsModal from './WinnerCongratulationsModal';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  BottomNavigation,
  BottomNavigationAction,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import PersonIcon from '@mui/icons-material/Person';
import EventIcon from '@mui/icons-material/Event';
import CampaignIcon from '@mui/icons-material/Campaign';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import PeopleIcon from '@mui/icons-material/People';
import BadgeIcon from '@mui/icons-material/Badge';
// import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import GroupsIcon from '@mui/icons-material/Groups';
import { memberPortalBranding } from '../../config/memberPortalBranding';
import { MemberTour, useMemberTour, TourHelpButton } from '../AppTour/AppTour';
import MemberNotificationListener from '../MemberNotificationListener';

const drawerWidth = 260;

const navItems = [
  { text: 'Dashboard', icon: <HomeIcon />, path: '/member-portal', exact: true },
  { text: 'Profile', icon: <PersonIcon />, path: '/member-portal/profile', exact: false },
  { text: 'Events', icon: <EventIcon />, path: '/member-portal/events', exact: false },
  { text: 'Announcements', icon: <CampaignIcon />, path: '/member-portal/announcements', exact: false },
  { text: 'Elections', icon: <HowToVoteIcon />, path: '/member-portal/elections', exact: false },
  { text: 'Executives', icon: <PeopleIcon />, path: '/member-portal/executives', exact: false },
  { text: 'My ID Card', icon: <BadgeIcon />, path: '/member-portal/card', exact: false },
  // { text: 'My Receipts', icon: <ReceiptLongIcon />, path: '/member-portal/receipts', exact: false }, // Commented out – payment disabled
];

// Bottom nav (mobile) - key items for thumb reach
const bottomNavItems = [
  { text: 'Home', icon: <HomeIcon />, path: '/member-portal', exact: true },
  { text: 'Events', icon: <EventIcon />, path: '/member-portal/events', exact: false },
  { text: 'News', icon: <CampaignIcon />, path: '/member-portal/announcements', exact: false },
  { text: 'Profile', icon: <PersonIcon />, path: '/member-portal/profile', exact: false },
];

export default function MemberPortalLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [winnerData, setWinnerData] = useState(null);
  const [showWinnerModal, setShowWinnerModal] = useState(false);
  const { run: runTour, startTour, shouldRunFirstTime } = useMemberTour();
  const [runFirstTime, setRunFirstTime] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (shouldRunFirstTime() && !runFirstTime) {
      setRunFirstTime(true);
      const t = setTimeout(() => startTour(), 200);
      return () => clearTimeout(t);
    }
  }, [shouldRunFirstTime, runFirstTime, startTour]);

  useEffect(() => {
    const token = localStorage.getItem('memberToken');
    if (!token) return;
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    api
      .get('/member-portal/elections/winner-status')
      .then((res) => {
        const data = res.data?.data;
        if (data?.show_congratulations) {
          setWinnerData(data);
          setShowWinnerModal(true);
        }
      })
      .catch((err) => {
        console.warn('Winner status fetch failed:', err?.response?.status, err?.response?.data);
      });
  }, []);

  const handleWinnerSeen = async () => {
    if (!winnerData?.election_id) return;
    try {
      await api.post('/member-portal/elections/winner-congratulations-seen', {
        election_id: winnerData.election_id,
      });
    } catch {
      // ignore
    }
    setWinnerData(null);
    setShowWinnerModal(false);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavClick = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('memberToken');
    localStorage.removeItem('member');
    navigate('/member-portal/login');
  };

  const isActive = (path, exact = false) => {
    if (path === '/member-portal') {
      return location.pathname === '/member-portal' || location.pathname === '/member-portal/';
    }
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const bottomNavValue = Math.max(
    0,
    bottomNavItems.findIndex((item) => isActive(item.path, item.exact))
  );

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar sx={{ bgcolor: 'primary.main', color: 'white', minHeight: 64, gap: 1.5 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1,
            bgcolor: 'rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <GroupsIcon sx={{ fontSize: 22 }} />
        </Box>
        <Typography variant="h6" noWrap component="div" fontWeight={600}>
          {memberPortalBranding.associationName}
        </Typography>
      </Toolbar>
      <Divider />
      <List sx={{ flex: 1, py: 1 }} data-tour="member-nav">
        {navItems.map((item) => (
          <ListItem key={item.path} disablePadding>
            <ListItemButton
              onClick={() => handleNavClick(item.path)}
              selected={isActive(item.path, item.exact)}
              sx={{
                borderRadius: 1,
                mx: 1,
                mb: 0.5,
                '&.Mui-selected': {
                  bgcolor: 'primary.light',
                  color: 'primary.contrastText',
                  '&:hover': { bgcolor: 'primary.main' },
                  '& .MuiListItemIcon-root': { color: 'inherit' },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  color: isActive(item.path, item.exact) ? 'inherit' : 'action.active',
                  minWidth: 40,
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
      <List>
        <ListItem disablePadding>
          <ListItemButton
            onClick={handleLogout}
            sx={{ borderRadius: 1, mx: 1, mb: 1 }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <ExitToAppIcon />
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <MemberTour run={runTour} onComplete={() => {}} />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: isMobile ? '100%' : `calc(100% - ${drawerWidth}px)` },
          ml: { sm: isMobile ? 0 : `${drawerWidth}px` },
          bgcolor: 'primary.main',
          color: 'white',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', minHeight: 64 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <IconButton
              color="inherit"
              aria-label="open menu"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ display: { sm: 'block', md: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1,
              bgcolor: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <GroupsIcon sx={{ fontSize: 20 }} />
          </Box>
          <Typography variant="h6" noWrap fontWeight={600}>
            {memberPortalBranding.associationName}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <TourHelpButton
            onClick={startTour}
            sx={{
              color: 'inherit',
              borderColor: 'rgba(255,255,255,0.7)',
              '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
            }}
          />
          <IconButton color="inherit" onClick={handleLogout} aria-label="logout">
            <ExitToAppIcon />
          </IconButton>
        </Box>
        </Toolbar>
      </AppBar>

      {/* Desktop sidebar */}
      {!isMobile && (
        <Box
          component="nav"
          sx={{ width: drawerWidth, flexShrink: 0 }}
        >
          <Drawer
            variant="permanent"
            sx={{
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: drawerWidth,
                top: 0,
                height: '100%',
                borderRight: 0,
              },
            }}
            open
          >
            {drawer}
          </Drawer>
        </Box>
      )}

      {/* Mobile drawer (temporary) */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              top: 64,
            },
          }}
        >
          {drawer}
        </Drawer>
      )}

      <WinnerCongratulationsModal
        open={showWinnerModal && !!winnerData}
        onClose={() => setShowWinnerModal(false)}
        electionName={winnerData?.election_name}
        positionsWon={winnerData?.positions_won || []}
        onSeen={handleWinnerSeen}
      />

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          width: { xs: '100%', md: `calc(100% - ${drawerWidth}px)` },
          ml: { xs: 0, md: `${drawerWidth}px` },
          mt: '64px',
          pb: { xs: 'calc(56px + env(safe-area-inset-bottom, 0px))', sm: 3 },
        }}
      >
        <MemberNotificationListener />
        <Outlet />
      </Box>

      {/* Mobile bottom navigation */}
      {isMobile && (
        <BottomNavigation
          value={bottomNavValue}
          onChange={(e, newValue) => {
            handleNavClick(bottomNavItems[newValue].path);
          }}
          showLabels
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            borderTop: 1,
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            boxShadow: '0 -2px 10px rgba(0,0,0,0.08)',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            '& .MuiBottomNavigationAction-root': {
              minWidth: 64,
            },
            '& .MuiBottomNavigationAction-root.Mui-selected': {
              color: 'primary.main',
            },
          }}
        >
          {bottomNavItems.map((item) => (
            <BottomNavigationAction
              key={item.path}
              label={item.text}
              icon={item.icon}
            />
          ))}
        </BottomNavigation>
      )}
    </Box>
  );
}
